import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Grid,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  CircularProgress,
  Box,
  Divider,
  styled,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  Badge
} from "@mui/material";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import axios from 'axios';

// Import Material UI Icons
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import HotelIcon from '@mui/icons-material/Hotel';
import BathtubIcon from '@mui/icons-material/Bathtub';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import KingBedIcon from '@mui/icons-material/KingBed';
import SingleBedIcon from '@mui/icons-material/SingleBed';
import WifiIcon from '@mui/icons-material/Wifi';
import WcIcon from '@mui/icons-material/Wc';
import HomeIcon from '@mui/icons-material/Home';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GroupIcon from '@mui/icons-material/Group';
import CategoryIcon from '@mui/icons-material/Category';

// Options for dropdowns
const propertyAmenitiesOptions = [
 "FreeParking",
 "Free Breakfast",
 "Pet Friendly",
 "Air Conditioning",
  "Laundry",
  "Newspaper",
  "Room Service",
  "Smoke Detector",
  "Swimming Pool",
  "Wifi",
  "Lounge",
  "Reception",
  "Bar",
  "Restaurant",
  "Luggage Assistance",
  "Wheelchair Accessibility",
  "Gym/Fitness Centre",
  "CCTV",
  "Fire Extinguishers",
  "First-aid Services",
  "Airport Transfers",
  "Basic Facilities",
  "Elevator/Lift",
  "Housekeeping",
  "Kitchen/Kitchenette",
  "LAN",
  "Power Backup",
  "Genset",
  "Refrigerator",
  "Umbrellas",
  "Washing Machine",
  "Laundromat",
  "EV Charging Station",
  "Driver's Accommodation",
  "General Services",
  "Bellboy Service",
  "Caretaker",
  "Concierge",
  "Multilingual Staff",
  "Luggage Storage",
  "Specially Abled Assistance",
  "Wake-up Call/Service",
  "Butler Services",
  "Doctor on Call",
  "Medical Centre",
  "Pool/Beach Towels",
  "Outdoor Activities and Sports",
  "Beach",
  "Bonfire",
  "Golf",
  "Kayaks",
  "Outdoor Sports (Cricket, Volleyball, Basketball, Tennis, Badminton)",
  "Swings",
  "Rock Climbing",
  "Archery",
  "Hiking",
  "Squash",
  "Snorkelling",
  "Telescope",
  "Water Sports",
  "Water Park",
  "Diving",
  "Canoeing",
  "Skiing",
  "Jungle Safari",
  "Cycling",
  "Common Areas",
  "Balcony/Terrace",
  "Fireplace",
  "Indoor Seating Area",
  "Lawn",
  "Library",
  "Sun Deck",
  "Verandah",
  "Jacuzzi",
  "Prayer Room",
  "Living Room",
  "Outdoor Furniture",
  "Picnic Area",
  "Game Room",
  "Sitout Area",
  "Bonfire Pit",
  "Food and Drink",
  "Barbecue",
  "Café",
  "Coffee Shop",
  "Dining Area",
  "Kid's Menu",
  "Bakery",
  "Breakfast",
  "Food Options Available",
  "Business Center and Conferences",
  "Banquet",
  "Business Center",
  "Conference Room",
  "Photocopying",
  "Fax Service",
  "Printer"
];

// Styled components for modern UI
const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
  overflow: 'hidden',
  padding: 0,
  background: '#fff',
  transition: 'transform 0.3s ease-in-out',
}));

const FormHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF7700 30%, #FF9800 90%)',
  color: '#fff',
  padding: theme.spacing(4, 2),
  textAlign: 'center',
  borderRadius: '12px 12px 0 0',
}));

const FormContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  marginBottom: theme.spacing(3),
  position: 'relative',
  paddingBottom: theme.spacing(1),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 50,
    height: 3,
    background: '#FF7700',
    borderRadius: 3,
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  background: '#f9f9f9',
  borderRadius: '8px',
  marginBottom: theme.spacing(3),
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  minHeight: 64,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    minWidth: 'auto',
    padding: theme.spacing(1),
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: 8,
  background: '#f9f9f9',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: '#FF7700',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#FF7700',
    },
  },
  '& .MuiFormLabel-root.Mui-focused': {
    color: '#FF7700',
  },
  marginBottom: theme.spacing(1.5),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF7700 30%, #FF9800 90%)',
  border: 0,
  borderRadius: 50,
  boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
  color: 'white',
  height: 54,
  padding: theme.spacing(0, 5),
  marginTop: theme.spacing(3),
  '&:hover': {
    background: 'linear-gradient(45deg, #FF5722 30%, #FF7700 90%)',
  },
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #ccc',
  borderRadius: 8,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  marginBottom: theme.spacing(2),
  '&:hover': {
    borderColor: '#FF7700',
    backgroundColor: 'rgba(255, 119, 0, 0.05)',
  },
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const PreviewImage = styled('img')(({ theme }) => ({
  width: 100,
  height: 100,
  objectFit: 'cover',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const ImageItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  '&:hover .MuiIconButton-root': {
    opacity: 1,
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -10,
  right: -10,
  background: '#fff',
  padding: 4,
  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  '&:hover': {
    background: '#f44336',
    color: '#fff',
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

// Helper to calculate SHA-256 hash of a file (returns hex string)
async function hashFileSHA256(file) {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Helper to hash an image URL (fetches as blob)
async function hashImageUrlSHA256(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return await hashFileSHA256(blob);
}

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return {};
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return {};
  }
}

const AddImage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for tabs
  const [activeTab, setActiveTab] = useState(0);
  const [hotelId, setHotelId] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", severity: "info" });
  
  // Dynamic room types state
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypesLoading, setRoomTypesLoading] = useState(false);
  
  // Dynamic room data state based on room types from API
  const [roomData, setRoomData] = useState({});
  
  // Dynamic images state based on room types from API
  const [images, setImages] = useState({
    bathroom: [],
    reception: [],
    hotel_images: [],
    main_image: [],
    cancel_check_photo: [],
    gst_photo: [],
    aadhar_card_photo: [],
  });

  const [existingHotelImageHashes, setExistingHotelImageHashes] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const isHashingRef = useRef(false);

  // Fetch room types from API
  const fetchRoomTypes = async (hotelId) => {
    if (!hotelId) return;
    
    setRoomTypesLoading(true);
    try {
      const token = getDecryptedToken();
      if (!token) {
        setNotification({
          show: true,
          message: "Please log in to view room types",
          severity: "error"
        });
        return;
      }

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAllRoomTypes',
        { token, hotel_id: parseInt(hotelId) },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status && Array.isArray(response.data.rooms_details)) {
        const fetchedRoomTypes = response.data.rooms_details;
        setRoomTypes(fetchedRoomTypes);
        
        // Initialize room data state with dynamic room types
        const initialRoomData = {};
        const initialImages = { ...images };
        
        fetchedRoomTypes.forEach(room => {
          const roomKey = room.category_name;
          initialRoomData[`${roomKey}_count`] = "";
          initialRoomData[`${roomKey}_occupancy`] = "";
          initialRoomData[`${roomKey}_amenities`] = [];
          initialImages[roomKey] = [];
        });
        
        setRoomData(initialRoomData);
        setImages(initialImages);
        
        console.log('Dynamic room types loaded:', fetchedRoomTypes);
        console.log('Initialized room data:', initialRoomData);
        console.log('Initialized images state:', initialImages);
      } else {
        setRoomTypes([]);
        setNotification({
          show: true,
          message: response.data.message || "Failed to fetch room types",
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Error fetching room types:", error);
      setNotification({
        show: true,
        message: "An error occurred while fetching room types",
        severity: "error"
      });
      setRoomTypes([]);
    } finally {
      setRoomTypesLoading(false);
    }
  };

  // Extract hotel_id from query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("hotel_id");
    if (id) {
      setHotelId(id);
      fetchRoomTypes(id); // Fetch room types when hotel ID is set
    } else {
      const manualId = prompt("Hotel ID not found. Please enter it manually:");
      if (manualId) {
        setHotelId(manualId);
        fetchRoomTypes(manualId); // Fetch room types when manual ID is entered
      } else {
        setNotification({
          show: true,
          message: "Hotel ID is required to proceed.",
          severity: "error"
        });
      }
    }
  }, [location]);
  
  // Helper to fetch latest hotel images from API
  const fetchHotelImagesFromApi = async (hotelId) => {
    try {
      setLoadingImages(true);
      const token = getDecryptedToken();
      if (!token) return {};
      const formData = new URLSearchParams({ hotel_id: hotelId, token });
      const response = await fetch('https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data && data.hotel_details && data.hotel_details.hotel_images) {
        return JSON.parse(data.hotel_details.hotel_images);
      }
    } catch (e) {}
    setLoadingImages(false);
    return {};
  };

  // Function to refresh images from API and update state
  const refreshHotelImages = async () => {
    if (!hotelId) return;
    setLoadingImages(true);
    const hotelImages = await fetchHotelImagesFromApi(hotelId);
    setImages((prev) => ({ ...prev, hotel_images: hotelImages.hotel_images || [] }));
    setLoadingImages(false);
  };

  // Hash all existing hotel_images on mount or when images.hotel_images changes
  useEffect(() => {
    async function hashExistingHotelImages() {
      if (!images.hotel_images || images.hotel_images.length === 0) {
        setExistingHotelImageHashes([]);
        return;
      }
      isHashingRef.current = true;
      const urls = images.hotel_images.filter(img => typeof img === 'string');
      const hashes = await Promise.all(urls.map(async (imgUrl) => {
        try {
          return await hashImageUrlSHA256(imgUrl);
        } catch {
          return null;
        }
      }));
      setExistingHotelImageHashes(hashes.filter(Boolean));
      isHashingRef.current = false;
    }
    hashExistingHotelImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.hotel_images]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setRoomData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle multi-select dropdown changes
  const handleMultiSelectChange = (e, field) => {
    const selectedValues = Array.isArray(e.target.value) ? e.target.value : [];
    setRoomData((prevData) => ({
      ...prevData,
      [field]: selectedValues,
    }));
  };

  // Handle image uploads
  const handleImageChange = async (e, category) => {
    const files = Array.from(e.target.files);
    // Validate file types
    const validFiles = files.filter((file) => file.type.startsWith("image/"));
    if (validFiles.length !== files.length) {
      setNotification({
        show: true,
        message: "Only image files are allowed.",
        severity: "error"
      });
      return;
    }
    // Duplicate check for hotel_images
    if (category === "hotel_images" && validFiles.length > 0) {
      // Always fetch and hash the latest server images before checking
      setLoadingImages(true);
      const hotelImagesFromApi = await fetchHotelImagesFromApi(hotelId);
      const serverHotelImages = hotelImagesFromApi.hotel_images || [];
      const serverImageUrls = serverHotelImages.filter(img => typeof img === 'string');
      const serverHashes = await Promise.all(serverImageUrls.map(async (imgUrl) => {
        try {
          return await hashImageUrlSHA256(imgUrl);
        } catch {
          return null;
        }
      }));
      setLoadingImages(false);
      for (const file of validFiles) {
        const fileHash = await hashFileSHA256(file);
        if (serverHashes.includes(fileHash)) {
          // toast.error('This image already exists (duplicate photo)'); // Disabled
          return; // Do not add this file
        }
      }
    }
    setImages((prevImages) => {
      if (category === "main_image") {
        // Only allow one file
        return { ...prevImages, [category]: validFiles.slice(0, 1) };
      }
      return {
      ...prevImages,
      [category]: [...prevImages[category], ...validFiles],
      };
    });
  };
  
  // Handle image delete
  const handleImageDelete = (category, index) => {
    setImages((prevImages) => {
      const updatedImages = [...prevImages[category]];
      updatedImages.splice(index, 1);
      return {
        ...prevImages,
        [category]: updatedImages,
      };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    try {
      // Validate hotel_id
      if (!hotelId) {
        setNotification({
          show: true,
          message: "Hotel ID is required to proceed.",
          severity: "error"
        });
        setLoading(false);
        return;
      }

      // Prepare FormData for API call
      const formData = new FormData();
      formData.append("hotel_id", hotelId);
      
      // Add dynamic room data based on room types from API
      roomTypes.forEach(room => {
        const roomKey = room.category_name;
        formData.append(`${roomKey}_count`, roomData[`${roomKey}_count`] || 0);
        formData.append(`${roomKey}_occupancy`, roomData[`${roomKey}_occupancy`] || "");
        formData.append(`${roomKey}_amenities`, roomData[`${roomKey}_amenities`].join(", ") || "");
      });

      // Append images
      for (const [category, files] of Object.entries(images)) {
        if (files && files.length > 0) {
          files.forEach((file, index) => {
            formData.append(`${category}[]`, file);
          });
        }
      }

      // Log FormData for debugging
      console.log("Sending image data to API:", Object.fromEntries(formData.entries()));

      // Make API call to upload images
      const response = await fetch("https://api.tabtrips.com/olivrweb/hotelapi/Api.php/setImages_new", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        setNotification({
          show: true,
          message: "Failed to upload images. Please try again.",
          severity: "error"
        });
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log("API Response:", data);

      if (data.status) {
        // Success message
        setNotification({
          show: true,
          message: "Property images and details added successfully!",
          severity: "success"
        });
        // Refresh images from API
        await refreshHotelImages();
        // Navigate to inventory after successful upload using URL parameter
        setTimeout(() => {
          navigate(`/rates-inventory/${hotelId}`);
        }, 2000);
      } else {
        setNotification({
          show: true,
          message: data.message || "Failed to upload images.",
          severity: "error"
        });
      }
      
    } catch (error) {
      console.error("Error during operation:", error);
      setNotification({
        show: true,
        message: "An error occurred while processing images.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // File upload components
  const FileUploadField = ({ category, label, icon, multiple = true }) => (
    <Box>
      <label htmlFor={`upload-${category}`}>
        <input
          id={`upload-${category}`}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={(e) => handleImageChange(e, category)}
          style={{ display: 'none' }}
        />
        <UploadBox component="span">
          {icon}
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>
            {label}
        </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Click to browse files
          </Typography>
          <Badge badgeContent={images[category]?.length || 0} color="primary" sx={{ mt: 1 }}>
            <PhotoLibraryIcon />
          </Badge>
        </UploadBox>
      </label>
      
      <ImagePreview>
        {images[category]?.map((file, index) => (
          <ImageItem key={index}>
            <PreviewImage
              src={URL.createObjectURL(file)}
              alt={`${category} preview ${index}`}
            />
            <DeleteButton size="small" onClick={() => handleImageDelete(category, index)}>
              <DeleteIcon fontSize="small" />
            </DeleteButton>
          </ImageItem>
        ))}
      </ImagePreview>
    </Box>
  );
  
  // Dynamic room amenities component
  const RoomAmenitiesField = ({ roomType, label }) => (
    <FormControl fullWidth variant="outlined">
      <InputLabel>{label} Amenities</InputLabel>
      <Select
        multiple
        value={roomData[`${roomType}_amenities`] || []}
        onChange={(e) => handleMultiSelectChange(e, `${roomType}_amenities`)}
        renderValue={(selected) => selected.join(", ")}
        label={`${label} Amenities`}
      >
        {propertyAmenitiesOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={(roomData[`${roomType}_amenities`] || []).includes(option)} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Dynamic room field component
  const DynamicRoomField = ({ room, index }) => {
    const roomKey = room.category_name;
    const roomLabel = room.category_name.replace(/_/g, ' ');
    
    return (
      <Grid item xs={12} sm={6} key={roomKey}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <KingBedIcon sx={{ mr: 1, color: '#FF7700' }} />
          <Typography variant="subtitle1" fontWeight="bold">{roomLabel}</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              label="Count"
              name={`${roomKey}_count`}
              type="number"
              value={roomData[`${roomKey}_count`] || ""}
              onChange={handleChange}
              required
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              label="Occupancy"
              name={`${roomKey}_occupancy`}
              value={roomData[`${roomKey}_occupancy`] || ""}
              onChange={handleChange}
              placeholder="e.g., 2 Adults, 1 Child"
              required
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <StyledContainer maxWidth="lg">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {notification.show && (
          <Alert 
            severity={notification.severity} 
            sx={{ mb: 2 }}
            onClose={() => setNotification({ ...notification, show: false })}
          >
            {notification.message}
          </Alert>
        )}
        
        <StyledPaper elevation={6}>
          <FormHeader>
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h4" component="h1" gutterBottom>
                Add Property Images & Details
        </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Hotel ID: <Box component="span" sx={{ fontWeight: 'bold', bgcolor: 'rgba(255,255,255,0.2)', px: 1, py: 0.5, borderRadius: 1 }}>{hotelId}</Box>
              </Typography>
            </motion.div>
          </FormHeader>
          
          <FormContent>
            <StyledTabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
              textColor="primary"
              indicatorColor="primary"
              aria-label="image upload tabs"
            >
              <StyledTab icon={<KingBedIcon />} label={!isMobile ? "Room Count & Occupancy" : ""} />
              <StyledTab icon={<WifiIcon />} label={!isMobile ? "Room Amenities" : ""} />
              <StyledTab icon={<PhotoLibraryIcon />} label={!isMobile ? "Room Images" : ""} />
              <StyledTab icon={<HomeIcon />} label={!isMobile ? "Property Images" : ""} />
              <StyledTab icon={<ReceiptIcon />} label={!isMobile ? "Documents" : ""} />
            </StyledTabs>

        <form onSubmit={handleSubmit}>
              {/* Tab 1: Room Count & Occupancy */}
              {activeTab === 0 && (
                <FormSection>
                  <SectionHeading variant="h5">
                    Room Counts & Occupancy
                  </SectionHeading>
                  
                  {roomTypesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : roomTypes.length === 0 ? (
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No room types found for this hotel. Please add room types first.
                    </Typography>
                  ) : (
                    <Grid container spacing={3}>
                      {roomTypes.map((room, index) => (
                        <DynamicRoomField key={room.category_name} room={room} index={index} />
                      ))}
                    </Grid>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setActiveTab(1)}
                        endIcon={<WifiIcon />}
                        sx={{
                          borderRadius: 28,
                          background: '#FF7700',
                          '&:hover': { background: '#E56900' }
                        }}
                        disabled={roomTypesLoading || roomTypes.length === 0}
                      >
                        Next: Room Amenities
                      </Button>
                    </motion.div>
                  </Box>
                </FormSection>
              )}
              
              {/* Tab 2: Room Amenities */}
              {activeTab === 1 && (
                <FormSection>
                  <SectionHeading variant="h5">
                    Room Amenities
                  </SectionHeading>
                  
                  {roomTypesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : roomTypes.length === 0 ? (
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No room types found for this hotel. Please add room types first.
                    </Typography>
                  ) : (
                    <Grid container spacing={3}>
                      {roomTypes.map((room) => {
                        const roomKey = room.category_name;
                        const roomLabel = room.category_name.replace(/_/g, ' ');
                        return (
                          <Grid item xs={12} md={6} key={roomKey}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <KingBedIcon sx={{ mr: 1, color: '#FF7700' }} />
                              <Typography variant="subtitle1" fontWeight="bold">{roomLabel}</Typography>
                            </Box>
                            <RoomAmenitiesField roomType={roomKey} label={roomLabel} />
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setActiveTab(0)}
                        sx={{ borderRadius: 28 }}
                      >
                        Back
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setActiveTab(2)}
                        endIcon={<PhotoLibraryIcon />}
                        sx={{
                          borderRadius: 28,
                          background: '#FF7700',
                          '&:hover': { background: '#E56900' }
                        }}
                        disabled={roomTypesLoading || roomTypes.length === 0}
                      >
                        Next: Room Images
                      </Button>
                    </motion.div>
                  </Box>
                </FormSection>
              )}
              
              {/* Tab 3: Room Images */}
              {activeTab === 2 && (
                <FormSection>
                  <SectionHeading variant="h5">
                    Room Images
                  </SectionHeading>
                  
                  {roomTypesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : roomTypes.length === 0 ? (
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No room types found for this hotel. Please add room types first.
                    </Typography>
                  ) : (
                    <Grid container spacing={3}>
                      {roomTypes.map((room) => {
                        const roomKey = room.category_name;
                        const roomLabel = room.category_name.replace(/_/g, ' ');
                        return (
                          <Grid item xs={12} md={6} key={roomKey}>
                            <FileUploadField 
                              category={roomKey} 
                              label={`${roomLabel} Images`}
                              icon={<KingBedIcon fontSize="large" sx={{ color: '#FF7700' }} />} 
                            />
                          </Grid>
                        );
                      })}
                      
                      <Grid item xs={12} md={6}>
                        <FileUploadField 
                          category="bathroom" 
                          label="Bathroom Images" 
                          icon={<BathtubIcon fontSize="large" sx={{ color: '#FF7700' }} />} 
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setActiveTab(1)}
                        sx={{ borderRadius: 28 }}
                      >
                        Back
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setActiveTab(3)}
                        endIcon={<HomeIcon />}
                        sx={{
                          borderRadius: 28,
                          background: '#FF7700',
                          '&:hover': { background: '#E56900' }
                        }}
                        disabled={roomTypesLoading || roomTypes.length === 0}
                      >
                        Next: Property Images
                      </Button>
                    </motion.div>
                  </Box>
                </FormSection>
              )}
              
              {/* Tab 4: Property Images */}
              {activeTab === 3 && (
                <FormSection>
                  <SectionHeading variant="h5">
                    Property Images
                  </SectionHeading>
                  
                  <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                      <FileUploadField
                        category="main_image"
                        label="Main Image (Cover Photo)"
                        icon={<PhotoLibraryIcon fontSize="large" sx={{ color: '#FF7700' }} />}
                        multiple={false}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FileUploadField 
                        category="hotel_images" 
                        label="Property Images" 
                        icon={<HomeIcon fontSize="large" sx={{ color: '#FF7700' }} />} 
                      />
                    </Grid>
                   
</Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setActiveTab(2)}
                        sx={{ borderRadius: 28 }}
                      >
                        Back
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setActiveTab(4)}
                        endIcon={<ReceiptIcon />}
                        sx={{
                          borderRadius: 28,
                          background: '#FF7700',
                          '&:hover': { background: '#E56900' }
                        }}
                      >
                        Next: Documents
                      </Button>
                    </motion.div>
                  </Box>
                </FormSection>
              )}
              
              {/* Tab 5: Documents */}
              {activeTab === 4 && (
                <FormSection>
                  <SectionHeading variant="h5">
                    Document Images
                  </SectionHeading>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <FileUploadField 
                        category="cancel_check_photo" 
                        label="Cancelled Check Photo" 
                        icon={<CreditCardIcon fontSize="large" sx={{ color: '#FF7700' }} />} 
                        multiple={false}
                      />
</Grid>

                    <Grid item xs={12} md={4}>
                      <FileUploadField 
                        category="gst_photo" 
                        label="GST Certificate" 
                        icon={<ReceiptIcon fontSize="large" sx={{ color: '#FF7700' }} />} 
                        multiple={false}
                      />
</Grid>

                    <Grid item xs={12} md={4}>
                      <FileUploadField 
                        category="aadhar_card_photo" 
                        label="Aadhar Card" 
                        icon={<PersonIcon fontSize="large" sx={{ color: '#FF7700' }} />} 
                        multiple={false}
                      />
                    </Grid>
</Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
  <Button
                        variant="outlined"
                        onClick={() => setActiveTab(3)}
                        sx={{ borderRadius: 28 }}
                      >
                        Back
  </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <SubmitButton
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      >
                        {loading ? "Uploading..." : "Submit All Images & Details"}
                      </SubmitButton>
                    </motion.div>
                  </Box>
                </FormSection>
              )}
            </form>
          </FormContent>
        </StyledPaper>
      </motion.div>
      {loadingImages && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}><CircularProgress color="primary" /></Box>}
    </StyledContainer>
  );
};

  export default AddImage;